import { graphql } from 'react-relay';
import { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl-next';
import debounce from 'lodash/debounce';

import type { CollabEditPlugin } from '@atlaskit/editor-plugin-collab-edit';
import type { ConnectivityPlugin } from '@atlaskit/editor-plugin-connectivity';
import { isOfflineMode } from '@atlaskit/editor-plugin-connectivity';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { SyncedBlockPlugin } from '@atlaskit/editor-plugin-synced-block';
import { usePluginStateEffect } from '@atlaskit/editor-common/use-plugin-state-effect';
import type { EditorActions } from '@atlaskit/editor-core';

import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { StatsigConfigurations } from '@confluence/statsig-client/entry-points/StatsigConfigurations.ts';
import { useNativeCollabState } from '@confluence/native-collab/entry-points/useNativeCollabState.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { setContentErrorAttributes } from '@confluence/monitoring/entry-points/error-attributes.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { getStatusCode } from '@confluence/error-types/entry-points/isStatusCodeError.ts';
import { retry } from '@confluence/retry';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';

import { showFlushSyncBlockErrorFlag } from '../../utils/showSyncBlockErrorFlag.tsx';

import type {
	useEditorDraftSyncActionEditorDraftSyncActionMutation as useEditorDraftSyncActionEditorDraftSyncActionMutation$data,
	useEditorDraftSyncActionEditorDraftSyncActionMutationVariables as useEditorDraftSyncActionEditorDraftSyncActionMutation$variables,
	useEditorDraftSyncActionEditorDraftSyncActionMutation_editorDraftSyncAction_EditorDraftSyncPayload_errors_MutationError_extensions_GenericMutationErrorExtension,
	useEditorDraftSyncActionEditorDraftSyncActionMutation_editorDraftSyncAction_EditorDraftSyncPayload_errors_MutationError,
} from './__types__/useEditorDraftSyncActionEditorDraftSyncActionMutation.ts';
import { DraftSyncResponseError } from './useDraftSync.tsx';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { mutationErrorExtensionsAggCompat } from '@confluence/apollo-relay-interop/entry-points/mutationErrorExtensionsAggCompat.ts';

export type SyncStatusMetadata = {
	status: string;
	version: number;
};

export type UseEditorDraftSyncActionProps = {
	contentId: string;
	editorPresetAPI?: PublicPluginAPI<
		[CollabEditPlugin, ConnectivityPlugin, SyncedBlockPlugin]
	> | null;
	editorActions: EditorActions;
	isFirstParticipant: boolean;
	onDraftSyncSuccess?: () => void; // a handler to be called when the draft sync is successful
	flags?: FlagsStateContainer;
};

const UNKNOWN_ERROR_STR = 'Unknown Error from useEditorDraftSyncAction Mutation';
const DRAFT_SYNC_DEBOUNCE_TIME = 2500;

const parseSyncStatus = (syncStatus: string | number | boolean): SyncStatusMetadata | undefined => {
	// syncStatus is expected to follow this pattern: pending-{stepVersionNumber} | synced-{stepVersionNumber}
	if (typeof syncStatus !== 'string') {
		return undefined;
	}
	const parsed = syncStatus.split('-');
	return {
		status: parsed[0],
		version: Number(parsed[1]),
	};
};

/**
 * Hook used to trigger draft sync for Live docs.
 * Draft sync is a step of the saving flow that writes the current state of the document to Confluence Monolith after a user makes changes.
 *
 * **WARNING**: This hook's functionality is only intended for Live docs, and its functionality should be skipped for Edit page.
 * Please ensure that any functionality added to this hook does not affect Edit page.
 *
 * @see useDraftSync for Edit page draft sync
 */
export const useEditorDraftSyncAction = ({
	contentId,
	editorPresetAPI,
	editorActions,
	isFirstParticipant,
	onDraftSyncSuccess,
	flags,
}: UseEditorDraftSyncActionProps) => {
	const intl = useIntl();
	const isLivePage = useIsCurrentPageLive();
	const [{ collabEditProvider, collabEditInitialized }, { setIsDraftSyncPending }] =
		useNativeCollabState();
	const draftSyncDebounceMaxWait = StatsigConfigurations.getNumber(
		'draft_sync_debounce_max_wait',
		0,
	);
	const [draftSyncActionError, setDraftSyncActionError] = useState<DraftSyncResponseError | null>(
		null,
	);
	const previousLocalChangeTimeRef = useRef<number | null | undefined>(undefined);
	const previousOfflineStatusRef = useRef<boolean>(false);

	const submitErrorToMonitoringSystem = useCallback(
		(error) => {
			setContentErrorAttributes(error, {
				objectId: contentId,
			});
			getMonitoringClient().submitError(error, {
				attribution: Attribution.PAGE_MODES,
			});
		},
		[contentId],
	);
	const [editorDraftSyncActionMutation] = useMutationInterop<
		useEditorDraftSyncActionEditorDraftSyncActionMutation$data,
		useEditorDraftSyncActionEditorDraftSyncActionMutation$variables
	>(graphql`
		mutation useEditorDraftSyncActionEditorDraftSyncActionMutation($input: EditorDraftSyncInput!) {
			editorDraftSyncAction(input: $input) {
				success
				errors {
					message
					extensions {
						errorType
						statusCode
					}
				}
			}
		}
	`);

	const executeDraftSyncActionMutation = useCallback(async () => {
		const currentStep = collabEditProvider?.getCurrentPmVersion();
		// Fallback adf should only be sent by a single user to avoid conflict errors on the BE
		const latestAdf = isFirstParticipant ? await editorActions.getValue() : null;
		const { data } = await editorDraftSyncActionMutation({
			variables: {
				input: {
					contentId,
					ncsStepVersion: currentStep,
					doSetRelations: true, // Collaborator relation is an entity on the backend used to indicate if a user made changes to that version
					latestAdf: latestAdf && JSON.stringify(latestAdf),
				},
			},
		});
		return data;
	}, [
		contentId,
		editorDraftSyncActionMutation,
		collabEditProvider,
		editorActions,
		isFirstParticipant,
	]);

	const saveDraft = useCallback(async () => {
		setIsDraftSyncPending(true);
		try {
			const data = await retry(executeDraftSyncActionMutation, { maxAttempts: 2, timeout: 2000 });
			const result = data?.editorDraftSyncAction;

			if (result?.success) {
				// After draft is saved, save source sync blocks and reference synced blocks on the document
				if (
					editorPresetAPI?.syncedBlock &&
					expValEquals('platform_synced_block', 'isEnabled', true)
				) {
					const flushSyncBlockSuccess: boolean =
						await editorPresetAPI.syncedBlock.actions.flushBodiedSyncBlocks();

					if (flags && !flushSyncBlockSuccess) {
						showFlushSyncBlockErrorFlag(
							flags,
							intl,
							editorPresetAPI.syncedBlock.actions.flushBodiedSyncBlocks,
						);
					}

					editorPresetAPI.syncedBlock.actions.flushSyncedBlocks();
				}

				setDraftSyncActionError(null);
				setIsDraftSyncPending(false);
				onDraftSyncSuccess?.();
				return;
			}

			if (result?.errors?.length && result?.errors?.length > 0) {
				const errors = mutationErrorExtensionsAggCompat(
					(result.errors ?? []).map((error) => ({
						...error,
						__typename: 'MutationError' as const,
					})),
				);
				errors.forEach((error, index) => {
					const originalError = result.errors?.[index];
					const typedError =
						error as unknown as useEditorDraftSyncActionEditorDraftSyncActionMutation_editorDraftSyncAction_EditorDraftSyncPayload_errors_MutationError & {
							extensions: Array<
								| useEditorDraftSyncActionEditorDraftSyncActionMutation_editorDraftSyncAction_EditorDraftSyncPayload_errors_MutationError_extensions_GenericMutationErrorExtension
								| null
								| undefined
							>;
							message: string | null | undefined;
						};
					const errorExtensionData = typedError?.extensions?.[0] as
						| useEditorDraftSyncActionEditorDraftSyncActionMutation_editorDraftSyncAction_EditorDraftSyncPayload_errors_MutationError_extensions_GenericMutationErrorExtension
						| undefined;
					const errorMessage = originalError?.message ?? typedError?.message;
					if (errorMessage && errorExtensionData?.statusCode) {
						setDraftSyncActionError(
							new DraftSyncResponseError(errorMessage, errorExtensionData.statusCode),
						);
					} else {
						setDraftSyncActionError(
							new DraftSyncResponseError(errorMessage || UNKNOWN_ERROR_STR, undefined),
						);
					}
					submitErrorToMonitoringSystem(originalError || typedError);
				});
				return;
			}
		} catch (error) {
			setDraftSyncActionError(
				new DraftSyncResponseError(
					error?.message || UNKNOWN_ERROR_STR,
					Number(getStatusCode(error)),
				),
			);
			submitErrorToMonitoringSystem(error);
			markErrorAsHandled(error);
		}
	}, [
		editorPresetAPI,
		submitErrorToMonitoringSystem,
		setIsDraftSyncPending,
		executeDraftSyncActionMutation,
		onDraftSyncSuccess,
		flags,
		intl,
	]);

	const onUpdate = useCallback(() => {
		if (!isLivePage || !collabEditProvider || !collabEditInitialized) {
			return;
		}

		const metadata = collabEditProvider.getMetadata();
		const collabSyncStatus = parseSyncStatus(metadata['syncStatus']);

		if (collabSyncStatus) {
			const currentStatus = collabSyncStatus['status'];
			if (currentStatus === 'synced') {
				// BE is not currently saving, so send request
				void saveDraft();
			}
			if (currentStatus === 'pending') {
				// implies BE is already saving, so we don't send a new request
				return;
			}
		} else {
			// collabSyncStatus is set by the BE as a result of draft sync. If it doesn't exist, BE needs to save and set it.
			void saveDraft();
		}
	}, [isLivePage, collabEditProvider, collabEditInitialized, saveDraft]);

	const debouncedOnUpdate = useMemo(
		() =>
			debounce(onUpdate, DRAFT_SYNC_DEBOUNCE_TIME, {
				trailing: true,
				...(draftSyncDebounceMaxWait > 0 && { maxWait: draftSyncDebounceMaxWait }),
			}),
		[onUpdate, draftSyncDebounceMaxWait],
	);

	const triggerUpdate = useCallback(() => {
		if (isLivePage) {
			void debouncedOnUpdate();
		}
	}, [debouncedOnUpdate, isLivePage]);

	const cancelUpdate = useCallback(() => debouncedOnUpdate.cancel(), [debouncedOnUpdate]);
	const flushUpdate = useCallback(() => {
		void debouncedOnUpdate.flush();
	}, [debouncedOnUpdate]);

	const previousRemoteChangeTimeRef = useRef<number | null | undefined>(undefined);

	const handlePluginStateEffect = useCallback(
		({ collabEditState, connectivityState }) => {
			if (!isLivePage) {
				return;
			}
			if (isOfflineMode(connectivityState?.mode)) {
				previousOfflineStatusRef.current = true;
				return;
			} else if (connectivityState?.mode === 'online') {
				// reset any draft sync errors upon coming back online
				if (previousOfflineStatusRef.current === true) {
					setDraftSyncActionError(null);
				}
				previousOfflineStatusRef.current = false;
			}
			const currentBodyChangeAt = collabEditState?.initialised?.lastLocalOrganicBodyChangeAt;
			const prevBodyChangeAt = previousLocalChangeTimeRef.current;
			if (currentBodyChangeAt && currentBodyChangeAt !== prevBodyChangeAt) {
				previousLocalChangeTimeRef.current = currentBodyChangeAt;
				triggerUpdate();
				return;
			}

			// When a synced block is edited inside an embed of the same page, the edit arrives
			// in the outer editor as a remote change (via NativeCollab). The iframe that
			// processed the edit gets destroyed before its debounced flush can fire.
			// To handle this, we flush synced blocks when remote organic body changes arrive,
			// since updateSyncBlockData marks blocks as dirty in the outer editor's store too.
			if (
				// eslint-disable-next-line confluence-feature-gating/no-preconditioning
				fg('platform_synced_block_patch_8') &&
				expValEquals('platform_synced_block', 'isEnabled', true)
			) {
				const currentRemoteBodyChangeAt =
					collabEditState?.initialised?.lastRemoteOrganicBodyChangeAt;
				const prevRemoteBodyChangeAt = previousRemoteChangeTimeRef.current;
				if (currentRemoteBodyChangeAt && currentRemoteBodyChangeAt !== prevRemoteBodyChangeAt) {
					previousRemoteChangeTimeRef.current = currentRemoteBodyChangeAt;
					void editorPresetAPI?.syncedBlock?.actions.flushBodiedSyncBlocks();
				}
			}
		},
		[isLivePage, triggerUpdate, editorPresetAPI],
	);
	usePluginStateEffect(editorPresetAPI, ['collabEdit', 'connectivity'], handlePluginStateEffect);

	useEffect(() => {
		if (!isLivePage || !collabEditProvider || !collabEditInitialized) {
			return;
		}
		window.addEventListener('beforeunload', flushUpdate);
		return () => {
			window.removeEventListener('beforeunload', flushUpdate);
		};
	}, [collabEditInitialized, collabEditProvider, isLivePage, flushUpdate]);

	return {
		triggerUpdate,
		cancelUpdate,
		flushUpdate,
		draftSyncActionError,
	};
};
